/**
 * Simple Markdown to HTML renderer
 * Handles common markdown syntax without external dependencies
 */

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} - Escaped text
 */
const escapeHtml = (text) => {
  const htmlEntities = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  };
  return String(text).replace(/[&<>"']/g, (char) => htmlEntities[char]);
};

/**
 * Parse inline markdown elements (bold, italic, code, links)
 * @param {string} text - Text to parse
 * @returns {string} - HTML string
 */
const parseInlineMarkdown = (text) => {
  let result = escapeHtml(text);

  // Bold: **text** or __text__
  result = result.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  result = result.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Italic: *text* or _text_ (but not inside words for underscore)
  result = result.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  result = result.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '<em>$1</em>');

  // Inline code: `code`
  result = result.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Links: [text](url)
  result = result.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // Line breaks: two spaces at end of line or explicit <br>
  result = result.replace(/ {2,}$/gm, '<br />');

  return result;
};

/**
 * Parse a single line and determine its type
 * @param {string} line - Line to parse
 * @returns {object} - { type, content, level }
 */
const parseLine = (line) => {
  const trimmedLine = line.trim();

  // Empty line
  if (!trimmedLine) {
    return { type: 'empty', content: '' };
  }

  // Headings: # Heading
  const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.+)$/);
  if (headingMatch) {
    return {
      type: 'heading',
      level: headingMatch[1].length,
      content: headingMatch[2],
    };
  }

  // Horizontal rule: ---, ***, ___
  if (/^[-*_]{3,}$/.test(trimmedLine)) {
    return { type: 'hr', content: '' };
  }

  // Unordered list: - item, * item, + item
  const ulMatch = trimmedLine.match(/^[-*+]\s+(.+)$/);
  if (ulMatch) {
    return { type: 'ul', content: ulMatch[1] };
  }

  // Ordered list: 1. item, 2. item
  const olMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
  if (olMatch) {
    return { type: 'ol', content: olMatch[2], number: parseInt(olMatch[1], 10) };
  }

  // Blockquote: > text
  const blockquoteMatch = trimmedLine.match(/^>\s*(.*)$/);
  if (blockquoteMatch) {
    return { type: 'blockquote', content: blockquoteMatch[1] };
  }

  // Code block fence: ```
  if (trimmedLine.startsWith('```')) {
    return { type: 'codefence', content: trimmedLine.slice(3).trim() };
  }

  // Regular paragraph
  return { type: 'paragraph', content: trimmedLine };
};

/**
 * Convert markdown text to HTML
 * @param {string} markdown - Markdown text
 * @returns {string} - HTML string
 */
export const renderMarkdownToHtml = (markdown) => {
  if (!markdown || typeof markdown !== 'string') {
    return '';
  }

  const lines = markdown.split('\n');
  const htmlParts = [];
  let inCodeBlock = false;
  let codeBlockContent = [];
  let codeBlockLanguage = '';
  let inList = null; // 'ul' or 'ol'
  let listItems = [];
  let inBlockquote = false;
  let blockquoteContent = [];

  const flushList = () => {
    if (inList && listItems.length > 0) {
      const tag = inList;
      const items = listItems
        .map((item) => `<li>${parseInlineMarkdown(item)}</li>`)
        .join('');
      htmlParts.push(`<${tag}>${items}</${tag}>`);
      listItems = [];
      inList = null;
    }
  };

  const flushBlockquote = () => {
    if (inBlockquote && blockquoteContent.length > 0) {
      const content = blockquoteContent
        .map((c) => parseInlineMarkdown(c))
        .join('<br />');
      htmlParts.push(`<blockquote>${content}</blockquote>`);
      blockquoteContent = [];
      inBlockquote = false;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Handle code blocks
    if (line.trim().startsWith('```')) {
      if (inCodeBlock) {
        // End code block
        const code = escapeHtml(codeBlockContent.join('\n'));
        const langClass = codeBlockLanguage ? ` class="language-${codeBlockLanguage}"` : '';
        htmlParts.push(`<pre><code${langClass}>${code}</code></pre>`);
        codeBlockContent = [];
        codeBlockLanguage = '';
        inCodeBlock = false;
      } else {
        // Start code block
        flushList();
        flushBlockquote();
        inCodeBlock = true;
        codeBlockLanguage = line.trim().slice(3).trim();
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockContent.push(line);
      continue;
    }

    const parsed = parseLine(line);

    // Handle list items
    if (parsed.type === 'ul' || parsed.type === 'ol') {
      flushBlockquote();
      if (inList !== parsed.type) {
        flushList();
        inList = parsed.type;
      }
      listItems.push(parsed.content);
      continue;
    } else {
      flushList();
    }

    // Handle blockquotes
    if (parsed.type === 'blockquote') {
      flushList();
      inBlockquote = true;
      blockquoteContent.push(parsed.content);
      continue;
    } else {
      flushBlockquote();
    }

    // Handle other types
    switch (parsed.type) {
      case 'empty':
        // Empty line - just add a break if we have content
        break;

      case 'heading':
        htmlParts.push(
          `<h${parsed.level}>${parseInlineMarkdown(parsed.content)}</h${parsed.level}>`
        );
        break;

      case 'hr':
        htmlParts.push('<hr />');
        break;

      case 'paragraph':
        htmlParts.push(`<p>${parseInlineMarkdown(parsed.content)}</p>`);
        break;

      default:
        break;
    }
  }

  // Flush any remaining content
  flushList();
  flushBlockquote();

  // Handle unclosed code block
  if (inCodeBlock && codeBlockContent.length > 0) {
    const code = escapeHtml(codeBlockContent.join('\n'));
    htmlParts.push(`<pre><code>${code}</code></pre>`);
  }

  return htmlParts.join('');
};

/**
 * React component to render markdown as HTML
 * Use this as a replacement for ReactMarkdown
 * @param {object} props - { children: string }
 * @returns {JSX.Element}
 */
export const SimpleMarkdown = ({ children }) => {
  if (!children || typeof children !== 'string') {
    return null;
  }

  const html = renderMarkdownToHtml(children);

  return (
    <div
      className="simple-markdown"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default SimpleMarkdown;
